<template>
    <div>

        <!-- Breadcrumb navigace (zobrazí se jen uvnitř složky) -->
        <div v-if="parent && parent.name" class="files-breadcrumb mb-4">
            <button class="btn cert-list__view-btn btn-sm" @click="getFoldersList()">
                <i class="bi bi-house-door me-1"></i>{{ $t('file.myFiles') }}
            </button>
            <span class="text-muted mx-2">/</span>
            <template v-if="parent.folders_path">
                <span v-for="ancestor in [...parent.folders_path].reverse()" :key="ancestor.uuid">
                    <button class="btn cert-list__view-btn btn-sm" @click="getFoldersList(ancestor.uuid)">
                        {{ ancestor.name }}
                    </button>
                    <span class="text-muted mx-2">/</span>
                </span>
            </template>
            <span class="fw-semibold" style="font-size:14px;">{{ parent.name }}</span>
        </div>

        <!-- Složky jako karty -->
        <div v-if="folders.length" class="folder-cards-row mb-4">
            <div
                v-for="folder in folders"
                :key="folder.uuid"
                class="folder-card dashboard-action-box--clickable"
                @click="getFoldersList(folder.uuid)"
            >
                <div class="folder-card__icon">
                    <img :src="iconFolderBlue" alt="">
                </div>
                <p class="folder-card__name">{{ folder.name }}</p>
                <p class="folder-card__count">&nbsp;</p>
            </div>
        </div>

        <Skeletor v-if="loading" />

        <!-- Prázdný stav -->
        <div v-if="!loading && !folders.length && !regularFiles.length" class="text-muted" style="font-size:14px;">
            {{ $t('general.noRecord') }}
        </div>

        <!-- Seznam souborů -->
        <div v-if="!loading && regularFiles.length" class="cert-list">
            <div
                class="cert-list__row"
                v-for="file in regularFiles"
                :key="file.uuid"
            >
                <div class="cert-list__icon-wrap">
                    <i :class="['bi', fileTypeIcon(file).cls, 'cert-list__file-icon']" :style="{ color: fileTypeIcon(file).color }"></i>
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('file.fileName') }}</span>
                    <span class="cert-list__value">{{ file.name }}</span>
                </div>

                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('file.fileCreatedDate') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(file.created_at) }}, {{ formatTime(file.created_at) }}</span>
                </div>

                <div class="cert-list__action">
                    <router-link
                        v-if="file.cert_uuid"
                        class="btn cert-list__view-btn"
                        :to="{ name: 'CertDetail', params: { uuid: file.cert_uuid } }"
                    >
                        {{ $t('cert.viewCertificate') }}
                    </router-link>
                    <button
                        v-else
                        class="btn cert-list__view-btn"
                        :disabled="creatingCertFor === file.uuid"
                        @click="createCertFromFile(file)"
                    >
                        {{ creatingCertFor === file.uuid ? $t('cert.processing') : $t('cert.certCreate') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Načíst více -->
        <div v-if="!loading && loadMoreAvailable" class="d-flex justify-content-center mt-4">
            <button class="btn cert-list__view-btn" @click="loadMore(parent?.id)">
                {{ $t('file.loadMore') }}
            </button>
        </div>

        <!-- Modal Vytvořit složku (nový design) -->
        <div v-if="showCreateFolderModal" class="create-cert-overlay" @click.self="showCreateFolderModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('file.newFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showCreateFolderModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <label class="create-cert-label mb-1">{{ $t('file.name') }}</label>
                    <input
                        type="text"
                        class="form-control mb-4"
                        v-model="newFolderName"
                        :placeholder="$t('file.newFolder')"
                        @keyup.enter="createFolder"
                    >
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-primary" @click="createFolder" :disabled="!newFolderName">
                            {{ $t('file.create') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import http from '@/http';
import { formatDateShort } from '@/utils/date.js';
import iconFolderBlue from '/public/img/new-design/icons/folder-fill-blue.svg';

export default {
    props: {
        selectedFolderUuid: {
            type: String,
            default: null,
        },
    },

    mounted() {
        this.getFoldersList();
        this.emitter.on('fileUploaded', (raw) => {
            const file = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (file && file.uuid) {
                this.getFileDetail(file.uuid);
            }
        });
        this.emitter.on('certAdded', (newCert) => {
            if (newCert?.file?.uuid) {
                this.getFileDetail(newCert.file.uuid);
            }
        });
    },

    data() {
        return {
            files: [],
            parent: null,
            currentUuid: null,
            newFolderName: '',
            showCreateFolderModal: false,
            loading: true,
            page: 1,
            perPage: 20,
            loadMoreAvailable: false,
            creatingCertFor: null,
            iconFolderBlue,
        };
    },

    computed: {
        folders() {
            return this.files.filter(f => f.type === 'FOLDER');
        },

        regularFiles() {
            return this.files.filter(f => f.type !== 'FOLDER');
        },
    },

    methods: {
        fileTypeIcon(file) {
            const mime = file?.mime_type ?? '';
            const ext = (file?.extension || (file?.filename ?? '').split('.').pop() || '').toLowerCase();
            if (mime === 'application/pdf' || ext === 'pdf')
                return { cls: 'bi-file-earmark-pdf', color: '#e74c3c' };
            if (mime.startsWith('image/') || ['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','svg'].includes(ext))
                return { cls: 'bi-file-earmark-image', color: '#3498db' };
            if (mime.includes('wordprocessingml') || mime === 'application/msword' || ['doc','docx','odt'].includes(ext))
                return { cls: 'bi-file-earmark-word', color: '#2b579a' };
            if (mime.includes('spreadsheetml') || mime === 'application/vnd.ms-excel' || ['xls','xlsx','ods','csv'].includes(ext))
                return { cls: 'bi-file-earmark-excel', color: '#217346' };
            if (mime.includes('presentationml') || mime === 'application/vnd.ms-powerpoint' || ['ppt','pptx','odp'].includes(ext))
                return { cls: 'bi-file-earmark-ppt', color: '#d24726' };
            if (mime.startsWith('audio/') || ['mp3','wav','ogg','flac','aac','m4a'].includes(ext))
                return { cls: 'bi-file-earmark-music', color: '#1abc9c' };
            if (mime.startsWith('video/') || ['mp4','avi','mov','mkv','webm','m4v'].includes(ext))
                return { cls: 'bi-file-earmark-play', color: '#e67e22' };
            if (['zip','rar','7z','tar','gz','bz2'].includes(ext) || mime.includes('zip'))
                return { cls: 'bi-file-earmark-zip', color: '#8e44ad' };
            if (mime.startsWith('text/') || ['txt','md','rtf'].includes(ext))
                return { cls: 'bi-file-earmark-text', color: '#7f8c8d' };
            if (['php','js','ts','py','html','css','json','xml','sql'].includes(ext))
                return { cls: 'bi-file-earmark-code', color: '#27ae60' };
            return { cls: 'bi-file-earmark', color: '#6B7280' };
        },

        getFoldersList(uuid, append = false) {
            const path = uuid ? `/files/folders/${uuid}` : '/files/folders';
            const params = { perPage: this.perPage, page: this.page };

            if (!append) {
                this.loading = true;
                this.page = 1;
            }

            http.get(path, { params }).then((response) => {
                this.parent = response.data.folder ?? null;
                this.currentUuid = uuid ?? null;

                const items = response.data.files || [];

                if (append) {
                    this.files = this.files.concat(items);
                } else {
                    this.files = items;
                }

                this.loadMoreAvailable = items.length === this.perPage;
                this.loading = false;
            });
        },

        loadMore(parentId) {
            this.page++;
            this.getFoldersList(parentId ?? this.currentUuid, true);
        },

        createFolder() {
            if (!this.newFolderName) return;

            const body = {
                name: this.newFolderName,
                uuid: this.currentUuid ?? null,
            };

            http.post('/files/folders/create', body).then((response) => {
                if (response.data.file) {
                    this.files = [response.data.file, ...this.files].sort((a, b) => {
                        const ta = a.type ?? '';
                        const tb = b.type ?? '';
                        const cmp = tb.localeCompare(ta);
                        return cmp !== 0 ? cmp : (a.name ?? '').localeCompare(b.name ?? '');
                    });
                }
                this.showCreateFolderModal = false;
                this.newFolderName = '';
            });
        },

        getFileDetail(uuid) {
            http.get(`/files/${uuid}/detail`).then((response) => {
                if (response.data.file) {
                    this.files.push(response.data.file);
                }
            });
        },

        createCertFromFile(file) {
            this.creatingCertFor = file.uuid;
            http.post('/certs/create-from-file', { file_uuid: file.uuid, name: file.name })
                .then((response) => {
                    const cert = response.data.cert;
                    const idx = this.files.findIndex(f => f.uuid === file.uuid);
                    if (idx !== -1 && cert) {
                        this.files[idx] = { ...this.files[idx], cert_uuid: cert.uuid, cert_id: cert.id };
                    }
                    this.emitter.emit('certAdded', cert);
                    if (window.updateCreditCount) window.updateCreditCount();
                })
                .finally(() => {
                    this.creatingCertFor = null;
                });
        },

        openCreateFolderModal() {
            this.showCreateFolderModal = true;
        },

        // Sdílená funkce – zkratka dne podle jazyka aplikace
        formatDateShort,

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style scoped>
.files-breadcrumb {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    padding: 10px 0;
    font-size: 14px;
}
</style>